import { startOfDay, startOfTomorrow, addDays } from './dates.ts';
import { PRIORITY_LABELS } from '@shared/types';
import type { TaskWithRelations, GroupableField, TaskStatus } from '@shared/types';

export const STATUS_LABELS: Record<TaskStatus, string> = {
  inbox: 'Inbox',
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

export interface TaskGroup {
  key: string;
  label: string;
  tasks: TaskWithRelations[];
}

/**
 * Buckets a task for grouping.
 *
 * Due dates collapse into ranges rather than grouping on the raw timestamp,
 * which would put every task in a group of one.
 */
export function bucketFor(
  task: TaskWithRelations,
  groupBy: GroupableField,
  projectNames: Map<string, string>,
  now: Date = new Date(),
): { key: string; label: string } {
  switch (groupBy) {
    case 'project_id':
      return task.project_id
        ? { key: task.project_id, label: projectNames.get(task.project_id) ?? 'Unknown project' }
        : { key: 'none', label: 'No project' };

    case 'status':
      return { key: task.status, label: STATUS_LABELS[task.status] };

    case 'priority':
      return { key: String(task.priority), label: PRIORITY_LABELS[task.priority] };

    case 'due': {
      if (!task.due_at) return { key: 'none', label: 'No date' };
      const due = new Date(task.due_at).getTime();
      if (due < now.getTime()) return { key: 'overdue', label: 'Overdue' };
      if (due < startOfTomorrow(now).getTime()) return { key: 'today', label: 'Today' };
      if (due < addDays(startOfDay(now), 7).getTime()) return { key: 'week', label: 'Next 7 days' };
      return { key: 'later', label: 'Later' };
    }
  }
}

const DUE_ORDER = ['overdue', 'today', 'week', 'later', 'none'];

/**
 * Groups rows for display while preserving the server's ordering within each
 * group — the server already sorted them, and re-sorting here would be a second
 * definition of "sorted" that could disagree with the column headers.
 */
export function groupTasks(
  tasks: TaskWithRelations[],
  groupBy: GroupableField | null,
  projectNames: Map<string, string>,
  now: Date = new Date(),
): TaskGroup[] {
  if (!groupBy) return [{ key: 'all', label: '', tasks }];

  const buckets = new Map<string, TaskGroup>();

  for (const task of tasks) {
    const { key, label } = bucketFor(task, groupBy, projectNames, now);
    const existing = buckets.get(key);
    if (existing) existing.tasks.push(task);
    else buckets.set(key, { key, label, tasks: [task] });
  }

  const groups = [...buckets.values()];

  // Due buckets have an inherent order and priority reads high-to-low;
  // everything else is alphabetical.
  if (groupBy === 'due') {
    groups.sort((a, b) => DUE_ORDER.indexOf(a.key) - DUE_ORDER.indexOf(b.key));
  } else if (groupBy === 'priority') {
    groups.sort((a, b) => Number(b.key) - Number(a.key));
  } else {
    // "No project" sorts last rather than alphabetically among real names.
    groups.sort((a, b) => {
      if (a.key === 'none') return 1;
      if (b.key === 'none') return -1;
      return a.label.localeCompare(b.label);
    });
  }

  return groups;
}
